import { Typography } from '@mui/material';
import { Grid, TextField, Button, FormControlLabel, Checkbox, Skeleton, Box, Stack, Switch, InputAdornment } from '@mui/material';
import MainCard from 'components/MainCard';

import * as Yup from 'yup';
import { Formik } from 'formik';

import { useSelector, useDispatch } from 'react-redux';
import * as FirestoreDataService from './../../services/FirestoreDataService';
import { showAppAlert } from 'store/reducers/appreducer';
import { resolvePartnerModifySettingsAllowed } from 'utils/partnerUiCapabilities';
import { useContext, useEffect, useState } from 'react';

import { setDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import OfflineDialog from 'components/OfflineDialog';

const INITIAL_VALUES = {
  noMotionAlert: {
    cooldown: 0,
    dial: false,
    enable: false,
    duration: 0,
    submit: null
  }
};

const NoMotionAlertSettingsPage = () => {
  const { selectedDevice, appUser } = useSelector((state) => state.appreducer);
  const [noMotionAlertSettings, setNoMotionAlertSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const status = await FirestoreDataService.getDeviceStatus(selectedDevice);

      if (status.exists()) {
        const last_online_time = status.data()?.last_online_time;
        const offlineThreshold = 5 * 60 * 1000; // 5 minutes;
        const currentTime = Date.now();
        const deviceStatus = currentTime - last_online_time < offlineThreshold ? 'online' : 'offline';

        if (deviceStatus === 'offline') {
          setIsDialogOpen(true);
        }
      }
    })();
  }, []);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    console.log(selectedDevice);
    if (!selectedDevice) return;
    setLoading(true);
    (async () => {
      await FirestoreDataService.getDeviceSettings(selectedDevice)
        .then((documentSnap) => {
          if (documentSnap.exists()) {
            let noMotionAlertObject = documentSnap.data().no_motion_alert;
            // console.log('Fetched:-', noMotionAlertObject);
            setNoMotionAlertSettings(noMotionAlertObject);
          }
        })
        .catch((e) => {
          const msg = 'Failed to get no-motion alert settings!';
          dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
          console.info(msg, e);
        })
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [selectedDevice]);

  return (
    <>
      <Grid item xs={12} sm={12} md={8} lg={6}>
        <MainCard contentSX={{ p: 2.25 }} style={{ height: '100%' }}>
          <Typography variant="h6" color="textSecondary">
            No Motion Alert Settings
          </Typography>
          {loading ? (
            <>
              <Skeleton variant="rectangular" animation="wave" height={230} />
            </>
          ) : (
            <Formik
              initialValues={noMotionAlertSettings ? noMotionAlertSettings : INITIAL_VALUES}
              validationSchema={Yup.object().shape({
                motionAlert: Yup.object()
                  .shape({
                    cooldown: Yup.number(),
                    duration: Yup.number(),
                    enable: Yup.boolean(),
                    dial: Yup.boolean()
                  })
                  .required()
              })}
              onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                try {
                  setStatus({ success: false });
                  setSubmitting(true);

                  // Smart Monitor ui_capabilities (canEditDeviceSettings) and org flag
                  const canModifySettings = resolvePartnerModifySettingsAllowed(appUser);
                  if (!canModifySettings) {
                    dispatch(
                      showAppAlert({
                        show: true,
                        alertType: 'error',
                        alertMessage: 'Error: You do not have permission to save these settings.'
                      })
                    );
                    return;
                  }

                  let payload = { ...values };

                  if (values.duration < 60) {
                    payload = { ...values, duration: 60 };
                  }
                  if (values.duration > 36000) {
                    payload = { ...values, duration: 36000 };
                  }
                  // console.log('payload', payload);

                  const tasksRef = doc(db, 'tasks', selectedDevice);
                  setDoc(tasksRef, {
                    device_id: selectedDevice,
                    type: 'no_motion_alert',
                    user_id: appUser.email ? appUser.email : '-',
                    old_value: noMotionAlertSettings,
                    payload
                  })
                    .then((response) => {
                      // console.log('saved tasks!', response);
                      const msg = 'No-motion alert settings saved successfully !';
                      dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: msg }));
                    })
                    .catch((e) => {
                      const msg = 'Failed to save no motion alert settings!';
                      console.log(msg, e);
                      dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
                    });

                  /* await FirestoreDataService.updateDeviceSettingsDocument(selectedDevice, { no_motion_alert: noMotionAlertSettings })
                  .then(() => {
                    setNoMotionAlertSettings(noMotionAlertSettings);
                    //saving tasks for syncing with server
                    FirestoreDataService.setTasks(selectedDevice, 'no_motion_alert', noMotionAlertSettings).then(() => {
                      console.log('Saved Tasks!');
                    });
                    const msg = 'No Motion Alert Settings Saved successfully!';
                    // console.info(msg);
                    dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: msg }));
                  })
                  .catch((e) => {
                    const msg = 'Failed to save No Motion Alert Settings!';
                    console.info(msg, e);
                    dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
                  }); */
                } catch (err) {
                  dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: 'Failed to save No-motion alert settings' }));
                  setStatus({ success: false });
                  setErrors({ submit: err.message });
                  setSubmitting(false);
                }
              }}
            >
              {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Box sx={{ mt: 2 }}>
                    <Grid item xs={12} justifyContent="space-between" alignItems="center" sx={{ display: 'flex' }}>
                      <Typography variant="caption" color="textSecondary">
                        Continuous no-motion period to activate the alert.
                      </Typography>

                      <FormControlLabel
                        name="enable"
                        id="enable"
                        control={<Switch onChange={handleChange} checked={values.enable} />}
                        label="Enable"
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Stack spacing={1} style={{ marginTop: '16px' }}>
                        <TextField
                          disabled={!values.enable}
                          fullWidth
                          id="duration"
                          type="number"
                          value={values.duration}
                          name="duration"
                          onChange={handleChange}
                          placeholder="Enter Duration 60 - 36000 seconds"
                          label="Duration"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">seconds</InputAdornment>
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12} style={{ paddingTop: 8 }}>
                      <FormControlLabel
                        disabled={!values.enable}
                        name="dial"
                        id="dial"
                        control={<Checkbox onChange={handleChange} checked={values.dial} />}
                        label="Recieve Call"
                      />
                      <Button disableElevation style={{ float: 'right' }} type="submit" variant="contained" color="primary">
                        Save
                      </Button>
                    </Grid>
                  </Box>
                </form>
              )}
            </Formik>
          )}
        </MainCard>
      </Grid>
      <OfflineDialog isOpen={isDialogOpen} handleClose={handleDialogClose}></OfflineDialog>
    </>
  );
};

export default NoMotionAlertSettingsPage;
